import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import type { IconType } from 'react-icons';

/**
 * @useFor "nothing here yet" placeholder content with an optional
 * call-to-action — empty tables, empty drawers, empty filtered lists,
 * "first-time" zero-states.
 *
 * Use this whenever a content region would otherwise render as
 * blank. An EmptyState always gives the user *something* to read or
 * do; bare blank panels are a UX anti-pattern this primitive exists
 * to prevent.
 *
 * Examples in-tree:
 *   - DataTable shows it when `rows.length === 0`
 *   - Sites Protocols list when a site has no protocols
 *   - VendorOrgSelector when the user has no orgs to switch to
 *
 * **Do NOT use** for:
 *   - **Loading states** — use `LoadingState` instead. EmptyState is
 *     for "we know there's nothing to show"; LoadingState is for
 *     "we don't know yet."
 *   - **Error states** — wrap whatever component owns error rendering
 *     (or use `StatusBadge` + a short retry CTA). EmptyState is for
 *     valid empty results, not failures.
 *   - **First-paint skeletons** — use skeleton primitives.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
export interface EmptyStateProps {
  icon?: IconType;
  title: string;
  description?: string;
  action?: React.ReactNode;
  e2eTitle?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  e2eTitle,
}) => {
  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      py={12}
      px={6}
      data-e2e={e2eTitle}
    >
      {Icon && (
        <Box
          fontSize="48px"
          color="text.tertiary"
          mb={4}
        >
          <Icon />
        </Box>
      )}
      <Text
        fontSize="lg"
        fontWeight={600}
        color="text.primary"
        mb={1}
        textAlign="center"
      >
        {title}
      </Text>
      {description && (
        <Text
          fontSize="sm"
          color="text.secondary"
          textAlign="center"
          maxW="360px"
        >
          {description}
        </Text>
      )}
      {action && <Box mt={4}>{action}</Box>}
    </Flex>
  );
};
